import { CSSObject } from '@emotion/react';
import * as React from 'react';

const buttonSizeStyles: Record<Required<ButtonProps>['size'], CSSObject> = {
  large: {
    fontSize: '16px',
    minHeight: '40px',
    paddingLeft: '32px',
    paddingRight: '32px',
  },
  small: {
    fontSize: '14px',
    minHeight: '32px',
    paddingLeft: '20px',
    paddingRight: '20px',
  },
};

const buttonVariantStyles: Record<ButtonProps['variant'], CSSObject> = {
  destructive: {
    backgroundColor: '#fafbfc',
    color: '#a71629',
    '&:disabled': {
      color: 'rgba(167, 22, 41, 0.5)',
    },
    '&:hover:not(:disabled)': {
      backgroundColor: 'rgba(198,110,122,0.1)',
    },
    '&:active:not(:disabled)': {
      backgroundcolor: 'rgba(167,22,41,0.1)',
      color: 'rgba(167, 22, 41, 0.75)',
    },
  },
  primary: {
    backgroundColor: '#0074cc',
    boxShadow: '0 2px 8px 0 rgba(0, 116, 204, 0.24)',
    color: '#fafbfc',
    '&:disabled': {
      backgroundColor: 'rgba(0, 116, 204, 0.5)',
    },
    '&:active:not(:disabled), &:hover:not(:disabled)': {
      backgroundColor: '#00579c',
    },
    '&:active:not(:disabled)': {
      color: 'rgba(250,251,252,0.75)',
    },
  },
  quaternary: {
    backgroundColor: '#a73f8e',
    boxShadow: '0 2px 8px 0 rgba(167, 63, 142, 0.24)',
    color: '#fafbfc',
    '&:active:not(:disabled), &:hover:not(:disabled)': {
      backgroundColor: '#732b62',
    },
    '&:active:not(:disabled)': {
      color: 'rgba(250,251,252,0.75)',
    },
  },
  secondary: {
    backgroundColor: '#fff',
    boxShadow: '0 2px 8px 0 rgba(0, 116, 204, 0.16)',
    color: '#0074cc',
    '&:disabled': {
      color: 'rgba(0, 116, 204, 0.5)',
    },
    '&:active:not(:disabled), &:hover:not(:disabled)': {
      backgroundColor: '#f4f7fc',
    },
    '&:active:not(:disabled)': {
      color: 'rgba(0,116,204,0.75)',
    },
  },
  tertiary: {
    backgroundColor: '#fafbfc',
    color: '#4d555c',
    '&:disabled': {
      backgroundColor: 'rgba(250, 251, 252, 0.5)',
      color: 'rgba(77, 85, 92, 0.5)',
    },
    '&:active:not(:disabled), &:hover:not(:disabled)': {
      backgroundColor: '#dcdfe3',
    },
    '&:active:not(:disabled)': {
      color: 'rgba(77,85,92,0.75)',
    },
  },
};

type ButtonProps = React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> & {
  /** The content of the button. */
  children: React.ReactNode;

  /** The desired height and width of the button. */
  size?: Size;

  /** The default behavior of the button. */
  type?: 'button' | 'submit';

  /** The aesthetic to apply to the button */
  variant: 'destructive' | 'primary' | 'quaternary' | 'secondary' | 'tertiary';
};

/**
 * Buttons are a type of **atom** that represent a `<button>` element.
 */
const Button: React.FC<ButtonProps> = ({ size, variant, ...rest }) => {
  return (
    <button
      css={[
        {
          alignItems: 'center',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'inline-flex',
          fontFamily: 'Roboto, sans-serif',
          '&:disabled': {
            cursor: 'default',
          },
        },
        buttonSizeStyles[size as Required<ButtonProps>['size']],
        buttonVariantStyles[variant],
      ]}
      {...rest}
    />
  );
};

Button.defaultProps = {
  size: 'small',
  type: 'button',
};

export default Button;
export type Size = 'large' | 'small';
